import type { Story } from '@nelnela/contracts';

/**
 * How the shelf and the bite talk about a piece (M6b steps 7 and 9).
 *
 * Two numbers, and both of them exist because the obvious version of each would be a lie.
 */

/**
 * Georgian words a minute for a learner reading with tap-to-gloss. M6a's own figure, from the
 * arithmetic that fixed the read tile's "~5 min": 320 words is about nine minutes for an L2
 * reader at this rate once the end checks are counted.
 */
export const READ_WPM = 55;

/** The two end checks. A piece is not over when the last sentence is. */
export const CHECK_MINUTES = 1;

/**
 * How long this piece actually takes — never `READ_MINUTES`.
 *
 * M6a's constant is 5, denominated on a generated story of ~145 Georgian words. The authored
 * pieces are shorter than that: measured over the 45 in `pieces.ka.json`, a story is 108 words at
 * the median (72–157) and a dialogue 77 (58–150), because they land at ~6.3 Georgian words a
 * sentence rather than the 8 they are asked for. Reusing the constant would print "~5 min" on a
 * two-minute read — the same lie M6a caught in this very tile and fixed by changing the number
 * rather than the label.
 *
 * Floored at 2: a one-minute label on something with two comprehension checks after it is a
 * promise the reader will not keep, and rounding can reach 1 for the shortest dialogue.
 */
export function readMinutes(piece: Pick<Story, 'sentences' | 'kind'>): number {
  const words = piece.sentences.reduce((n, s) => n + s.text.split(/\s+/).filter(Boolean).length, 0);
  // A culture note is English prose read at native speed, not Georgian read with a gloss sheet
  // open. Charging it the L2 rate would put "4 min" on the tile that says "1 min · about the show
  // you're living in", and the tile is right.
  if (piece.kind === 'culture') return 1;
  return Math.max(2, Math.round(words / READ_WPM) + CHECK_MINUTES);
}

/**
 * The level as a BAND, never a bare number.
 *
 * Two reasons, and the second is the load-bearing one. A bare "level 24" invites a precision the
 * catalog cannot support — it tops out at 60 with a compressed curve that M7 exists to re-anchor.
 * And right now the measured levels do not vary at all between pieces written for level 8 and
 * level 38 (`docs/M6B-HANDOFF.md` § "The level is not yet a level"), so a number would be a
 * confident statement of something untrue. A band degrades honestly when the underlying figure
 * improves, and says less when it should.
 *
 * Null — every culture note, and any piece the tagger could not place — says nothing at all
 * rather than guessing.
 */
export function shelfBand(level: number | null): string | null {
  if (level === null || !Number.isFinite(level)) return null;
  const lo = Math.floor((level - 1) / 10) * 10 + 1;
  return `levels ${lo}–${lo + 9}`;
}

// ---------------------------------------------------------------------------------------------
// M6e §8 — the read badge
// ---------------------------------------------------------------------------------------------

/** What a card says about how this learner has read it. `null` = say nothing. */
export type ReadBadge = 'new' | 'read' | 'read again' | 'left early';

/**
 * new · read · read again · left early.
 *
 * "Left early" OUTRANKS the read counts, and that ordering is the point of the badge: a piece the
 * learner walked out of is the one thing on the shelf worth pointing at, and saying "read" about
 * it would be worse than saying nothing. It comes from `abandoned` on the MOST RECENT ended
 * activity, so finishing a piece clears it — the badge is about where the learner is now, not
 * about everything that ever happened to the piece.
 *
 * `null` when the list did not carry read state at all (`storyGet`, a cached older response): an
 * absent count is not a zero, and badging an unknown as "new" is the one wrong answer here.
 */
export function readBadge(piece: Pick<Story, 'reads' | 'leftEarly'>): ReadBadge | null {
  if (piece.reads === undefined) return null;
  if (piece.leftEarly === true) return 'left early';
  if (piece.reads === 0) return 'new';
  return piece.reads === 1 ? 'read' : 'read again';
}

/** A read piece is dimmed on the shelf; one walked out of is not — it is unfinished business. */
export function shelfDimmed(piece: Pick<Story, 'reads' | 'leftEarly'>): boolean {
  return readBadge(piece) === 'read' || readBadge(piece) === 'read again';
}
